import { NuxtI18nInstance } from "@nuxtjs/i18n";
import { DEFAULT_PAGINATOR_SIZES, ROLE, WIDTH_NG } from "~/assets/js/constants";
import { createPaginator, isAllowedTo } from "~/assets/js/utils";
import { NewsItemDTO, NewsItemRow } from "~/types/newsItem/newsItem";
import {
  Action,
  Column,
  PaginatedResponse,
  Table,
  TableHeader,
  TableOptions,
  stateComponentOptions,
} from "~/types/types";

import sanitizeHtml from "sanitize-html";

export default class NewsItemsTransformer {
  static table(
    newsItems: PaginatedResponse<NewsItemDTO>,
    options: TableOptions,
  ): Table<NewsItemRow> {
    const { role, i18n } = options;

    const paginator = createPaginator<NewsItemDTO>(newsItems, i18n, DEFAULT_PAGINATOR_SIZES);

    const headers: TableHeader[] = [
      {
        id: "headline",
        label: i18n.t("news.headers.headline"),
        sortable: true,
      },
      {
        id: "body",
        label: i18n.t("news.headers.body"),
        sortable: true,
      },
      {
        id: "createdOn",
        label: i18n.t("news.headers.createdOn"),
        sortable: true,
      },
    ];

    return {
      paginator,
      sizes: [WIDTH_NG.XS, WIDTH_NG["3XL"], WIDTH_NG.LG],
      main: "headline",
      headers,
      rows: newsItems.collection.map((newsItem) => this.row(newsItem, { role, i18n })),
    };
  }

  static row(
    newsItem: NewsItemDTO,
    { role, i18n }: { role: ROLE; i18n: NuxtI18nInstance },
  ): NewsItemRow {
    const { body, createdOn, headline, id, vaccine } = newsItem;

    const actions: Action[] = [];

    if (isAllowedTo([ROLE.ADMIN, ROLE.SERVICEDESK], role)) {
      actions.push({
        active: false,
        id: "newsitem-edit",
        icon: "s2-icon-pen-regular",
        label: i18n.t(`generic.edit`),
        loading: false,
      });

      actions.push({
        active: false,
        id: "newsitem-delete",
        icon: "s2-icon-trash-regular",
        label: i18n.t(`generic.delete`),
        loading: false,
      });
    }

    const columns: Column[] = [
      {
        data: {
          label: headline,
        },
        id: "headline",
        type: "text",
      },
      {
        data: {
          label: sanitizeHtml(body, {
            allowedTags: [],
            allowedAttributes: {},
          }),
        },
        id: "body",
        type: "text",
      },
      {
        data: {
          label: i18n.d(new Date(createdOn * 1000), "medium"),
        },
        id: "createdOn",
        type: "text",
      },
    ];

    return {
      id,
      body,
      createdOn,
      headline,
      vaccine,
      columns,
      actions,
    };
  }

  static latestNewsTable(newsItems: PaginatedResponse<NewsItemDTO>, options: TableOptions) {
    const { i18n } = options;

    const headers: TableHeader[] = [
      {
        id: "headline",
        label: i18n.t("news.headers.headline"),
        sortable: false,
      },
      {
        id: "body",
        label: i18n.t("news.headers.body"),
        sortable: false,
      },
      {
        id: "createdOn",
        label: i18n.t("news.headers.createdOn"),
        sortable: false,
      },
      {
        id: "updatedOn",
        label: i18n.t("news.headers.updatedOn"),
        sortable: false,
      },
    ];

    const table = {
      headers,
      main: "headline",
      sizes: [WIDTH_NG.XS, WIDTH_NG["3XL"], WIDTH_NG.LG, WIDTH_NG.LG],
      rows: [...newsItems.collection.map((newsItem) => this.latestNewsRow(newsItem, { i18n }))],
    };

    return table;
  }

  static latestNewsRow(newsItem: NewsItemDTO, options: TableOptions): NewsItemRow {
    const { i18n } = options;
    const { body, createdOn, updatedOn, headline, id, readByUser, vaccine } = newsItem;

    const actions: Action[] = [];

    const columns: Column[] = [
      {
        data: {
          label: headline,
        },
        id: "headline",
        type: "text",
      },
      {
        data: {
          label: sanitizeHtml(body, {
            allowedTags: [],
            allowedAttributes: {},
          }),
        },
        id: "body",
        type: "text",
      },
      {
        data: {
          label: i18n.d(new Date(createdOn * 1000)),
        },
        id: "createdOn",
        type: "text",
      },
      {
        data: {
          label: i18n.d(new Date(updatedOn * 1000)),
        },
        id: "updatedOn",
        type: "text",
      },
    ];

    return {
      id,
      body,
      createdOn,
      updatedOn,
      headline,
      readByUser,
      vaccine,
      actions,
      columns,
      ...iconAndStateFromReadState(readByUser),
    };
  }
}

function iconAndStateFromReadState(readByUser: boolean): {
  icon?: string;
  state?: stateComponentOptions;
} {
  if (readByUser) {
    return { icon: "s2-icon-check-solid", state: "ok" };
  } else {
    return { icon: "s2-icon-eye-solid", state: "ok" };
  }
}
